import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useVendorLibraryServiceCategories } from "./queries";
import {
  documentsFromLibraryServices,
  getLibraryServiceLocks,
  mergeLibraryDocumentServiceOptions,
  nextProviderCapabilitiesWithLibraryServices,
} from "../utils/vendorProfileCapabilities";

const EMPTY_RFIS = [];

/**
 * Library Documents services for vendor Overview badges and Company Profile
 * locks, without paging the full documents catalog.
 */
export function useVendorProfileLibraryServices(providerId) {
  const { data } = useVendorLibraryServiceCategories(providerId);
  const rfisFromStore = useSelector((state) => state.rfisColors?.rfis);
  const rfisList = rfisFromStore || EMPTY_RFIS;
  const libraryDocumentItems = useMemo(
    () => documentsFromLibraryServices(data?.items).items,
    [data]
  );
  const libraryDocuments = useMemo(
    () => ({ items: libraryDocumentItems }),
    [libraryDocumentItems]
  );
  const { lockedValues, lockedLabels } = useMemo(
    () => getLibraryServiceLocks(libraryDocuments, rfisList),
    [libraryDocuments, rfisList]
  );

  return {
    libraryDocumentItems,
    libraryDocuments,
    lockedValues,
    lockedLabels,
    rfisList,
    mergeSelected: (selected) =>
      mergeLibraryDocumentServiceOptions(selected, libraryDocuments, rfisList),
  };
}

/** Keep Library Documents services in the form value once the lightweight list loads. */
export function useSyncLibraryServicesIntoForm({
  enabled,
  getValues,
  setValue,
  libraryDocumentItems,
  rfisList,
}) {
  useEffect(() => {
    if (!enabled || !libraryDocumentItems?.length) return;
    const current = getValues("provider_capabilities") || [];
    const next = nextProviderCapabilitiesWithLibraryServices(
      current,
      { items: libraryDocumentItems },
      rfisList
    );
    if (next !== current) {
      setValue("provider_capabilities", next, { shouldDirty: false });
    }
  }, [enabled, libraryDocumentItems, rfisList, getValues, setValue]);
}

/**
 * Company Profile: library locks + keep Library Documents services in the form.
 */
export function useVendorOrganizationLibraryCapabilities({
  vendorId,
  enabled,
  getValues,
  setValue,
}) {
  const library = useVendorProfileLibraryServices(enabled ? vendorId : null);
  useSyncLibraryServicesIntoForm({
    enabled,
    getValues,
    setValue,
    libraryDocumentItems: library.libraryDocumentItems,
    rfisList: library.rfisList,
  });
  return library;
}
